import { useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { backButton } from '@tma.js/sdk-react';

import { ascoltaLivelli, frenoIndietro, livelloInCima, quantiLivelli } from '@/lib/livelliIndietro';

/**
 * Vero quando questa schermata NON ha una voce precedente da cui e' arrivata.
 *
 * `history.state.idx` e' l'indice con cui react-router numera le voci: `0` e' la prima, e uno
 * stato nullo vuol dire che la voce non l'ha scritta lui (deep link aperto dal bot, primo
 * ingresso nella mini app, o un `replace` che ha sostituito l'unica voce che c'era).
 * ⚠️ Si legge `window.history`, non il router: il router sa dove SEI, non da dove VIENI.
 */
export function senzaCronologia(): boolean {
  try {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    return idx == null || idx <= 0;
  } catch {
    // Storage/history inaccessibile: meglio il ripiego che un tasto muto.
    return true;
  }
}

/**
 * Wires the native Telegram BackButton to React Router.
 * On the home route the button is hidden — unless a layer (sheet, modal) is open; elsewhere it closes
 * the top layer if there is one, and otherwise pops the history.
 *
 * 🔴 E QUANDO NON C'E' UN INDIETRO, il tasto non resta muto. `navigate(-1)` su una schermata
 * raggiunta senza cronologia non fa NULLA: il bottone si vede, si preme, e non succede niente —
 * che per chi guarda e' indistinguibile da un'app bloccata. Capita piu' di quanto sembri: un
 * deep link aperto dal bot entra direttamente sulla rotta, e un `replace` puo' aver sostituito
 * l'unica voce (era il caso di Hub -> Battaglie, chiuso in #220: *«indietro che non torna» e
 * «indietro che non c'e'» sono lo stesso difetto visto da due schermate*).
 *
 * Il ripiego e' `navigate('/', { replace: true })`, e non e' inventato qui: `InviteLanding` lo
 * usa da mesi con la ragione scritta accanto (ADR-0115) — *il tasto indietro non deve poter
 * tornare su una rotta che si e' appena dichiarata inutilizzabile*. `replace` per lo stesso
 * motivo: si esce, non si impila una via di ritorno verso il vicolo.
 *
 * 🔴 **PRIMA I LIVELLI, POI LA CRONOLOGIA** (seguito di #434). Con un foglio (`Sheet`) o la modale del
 * check-in aperti, il tasto chiudeva il livello (smontato con la schermata) E tornava indietro di una
 * schermata. Ora se la pila di `lib/livelliIndietro.ts` ha una cima, il tasto chiude QUELLA e basta; a
 * pila vuota e' la cronologia di sempre. Sulla Hub il bottone si MOSTRA finche' c'e' un livello: nascosto,
 * il tasto di sistema chiuderebbe la mini app col foglio aperto.
 *
 * Mount once at the layout level (AppShell).
 */
export function useBackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const conLivelli = useSyncExternalStore(ascoltaLivelli, quantiLivelli) > 0;

  useEffect(() => {
    if (!backButton.isMounted()) return;

    const isHome = location.pathname === '/';
    if (isHome && !conLivelli) {
      backButton.hide();
      return;
    }
    backButton.show();
    const off = backButton.onClick(() => {
      // La cima si legge al TOCCO, non alla registrazione: fra le due un livello puo' aprirsi o chiudersi.
      const cima = livelloInCima();
      if (cima !== null) {
        cima();
        return;
      }
      // Il passo indietro che sta per partire: o la Hub (senza cronologia) o la voce precedente.
      const passo = () => {
        if (senzaCronologia()) {
          navigate('/', { replace: true });
          return;
        }
        navigate(-1);
      };
      // IL FRENO (I-402): dal tavolo di una partita viva col MIO orologio che corre il passo si CHIEDE prima
      // («Lasciare il tavolo?», `contexts/UscitaTavoloContext.tsx`); «Esci» esegue questo stesso `passo`. Letto al TOCCO,
      // dopo la pila: un foglio aperto — la conferma compresa — e' gia' stato chiuso qui sopra.
      if (frenoIndietro()?.(passo) === true) return;
      passo();
    });
    return () => {
      off();
    };
  }, [location.pathname, navigate, conLivelli]);
}
